<template>
  <div ref="chart" :style="{width, height}"></div>
</template>

<script>
import echarts from 'echarts';
const keyToTime = (key) => {
  if (!key) return '--';
  const text = String(key).trim().replace('T', ' ');
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(text)
    ? `${text}:00`
    : text;
  const ts = new Date(normalized.replace(/-/g, '/')).getTime();
  return Number.isNaN(ts) ? '--' : ts;
};

const DynamicEnvChart = {
  props: {
    switchPortIndex: {
      type: String,
      default: ''
    },
    dateRangeValue: {
      type: Array,
      default: () => []
    },
    // 单个数据系列 {"2019-01-04 19:00": 9.17, "2019-01-04 19:05": 15.15}
    // 或多个数据系列 {"2019-01-04 19:00": [9.17, 20.00], "2019-01-04 19:05": [15.15, 12.50]}
    electricCurrents: {
      type: [Object, Array],
      default: null
    },
    isEmpty: {
      type: Boolean,
      default: false
    },
    yAxisName: { // 纵坐标名称
      type: String,
      default: ''
    },
    seriesNames: { // 电量,上行流量,下行流量
      type: String,
      default: ''
    },
    legendNamesMap: {
      type: Object, // 非必须，如果存在则在图例中替换原系列名显示，如 {"上行流量":"动环信息","下行流量":"动环信息"}
      default: null
    },
    zoomBar: { // 是否使用缩放条组件
      type: Boolean,
      default: false
    },
    color: {
      type: Array,
      default () {
        return ['#2c68ff', '#28d1d1', '#3da8ff', '#ff9c00', '#a88ee6', '#ffd600',
          '#37de99', '#6572ab', '#ff576a', '#e65043', '#c4ccd3'];
      }
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '400px'
    },
    unit: {
      type: String,
      default: ''
    },
    periodTag: {// 父组件的周期
      type: String,
      default: '--'
    }
  },
  data () {
    return {
      chart: null,
      lastKeys: [],
      lastValues: [],
      needsRefresh: false
    };
  },
  computed: {
    seriesNamesArr () {
      return this.seriesNames.split(',');
    }
  },
  mounted () {
    this.renderChart(this.electricCurrents);

    window.onresize = () => (() => {
      this.chart.resize();
    })();
  },
  watch: {
    switchPortIndex() {
      this.needsRefresh = true;
    },
    dateRangeValue() {
      this.needsRefresh = true;
    },
    periodTag(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.needsRefresh = true;
      }
    },
    electricCurrents(newVal) {
      if (!newVal) {
        this.lastKeys = [];
        this.lastValues = [];
        this.renderChart(newVal);
        return;
      }

      const isFirstLoad = this.lastKeys.length === 0;
      // 搜索、端口切换、时间范围切换后：全量重绘
      if (this.needsRefresh || isFirstLoad) {
        this.lastKeys = [];
        this.lastValues = [];
        this.resetZoom();
        this.needsRefresh = false;
        this.$nextTick(() => {
          this.renderChart(newVal);
        });
        return;
      }

      const newKeys = Object.keys(newVal)
        .sort((a, b) => keyToTime(a) - keyToTime(b))
        .filter(item => Number.isFinite(keyToTime(item)));
      if (!newKeys.length) return;

      const oldKeys = this.lastKeys.filter(item => Number.isFinite(keyToTime(item)));
      if (!oldKeys.length) {
        this.replaceDataWindow(newVal);
        return;
      }

      const newFirstTs = keyToTime(newKeys[0]);
      const newLastTs = keyToTime(newKeys[newKeys.length - 1]);
      const oldFirstTs = keyToTime(oldKeys[0]);
      const oldLastTs = keyToTime(oldKeys[oldKeys.length - 1]);

      const shouldReplaceWindow =
        !Number.isFinite(newFirstTs) ||
        !Number.isFinite(newLastTs) ||
        !Number.isFinite(oldFirstTs) ||
        !Number.isFinite(oldLastTs) ||
        newKeys.length !== oldKeys.length ||
        newFirstTs < oldFirstTs ||
        newLastTs < oldLastTs ||
        newFirstTs > oldLastTs + 3600 * 1000;

      if (shouldReplaceWindow) {
        this.replaceDataWindow(newVal);
        return;
      }

      // 轮询追加（左移 + 右追加），并同步窗口内最新值
      this.appendNewData(newVal);
    }
  },
  methods: {
    resetZoom() {
      if (!this.chart) return;
      this.chart.dispatchAction({
        type: 'dataZoom',
        start: 0,
        end: 100
      });
    },
    normalizePointValue(pointVal) {
      const valArr = Array.isArray(pointVal) ? pointVal : [pointVal];
      return valArr.map((v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n.toFixed(2) : '--';
      });
    },
    applyWindowToChart() {
      if (!this.chart) return;
      this.chart.setOption({
        xAxis: { data: this.lastKeys },
        series: this.seriesNamesArr.map((name, idx) => ({
          type: 'line',
          data: this.lastValues.map((v) => {
            const point = v[idx];
            if (point === '--' || point === undefined || point === null) return null;
            const n = Number(point);
            return Number.isFinite(n) ? n : null;
          })
        }))
      });
    },
    replaceDataWindow(newData) {
      if (!this.chart || !newData) return;
      const sortedKeys = Object.keys(newData)
        .sort((a, b) => keyToTime(a) - keyToTime(b))
        .filter(item => Number.isFinite(keyToTime(item)));
      if (!sortedKeys.length) return;

      this.lastKeys = sortedKeys;
      this.lastValues = sortedKeys.map(timeKey => this.normalizePointValue(newData[timeKey]));
      this.applyWindowToChart();
    },
    appendNewData(newData) {
      try {
        if (!this.chart || !newData) return;
        const sortedKeys = Object.keys(newData)
          .sort((a, b) => keyToTime(a) - keyToTime(b))
          .filter(item => Number.isFinite(keyToTime(item)));
        if (!sortedKeys.length) return;

        const oldLastTs = keyToTime(this.lastKeys[this.lastKeys.length - 1]);
        const incomingKeys = sortedKeys.filter((timeKey) => {
          const ts = keyToTime(timeKey);
          if (!Number.isFinite(ts)) return false;
          if (!Number.isFinite(oldLastTs)) return true;
          return ts > oldLastTs;
        });

        const maxLen = this.lastKeys.length || sortedKeys.length;

        incomingKeys.forEach((timeKey) => {
          const newVal = this.normalizePointValue(newData[timeKey]);

          if (this.lastKeys.length >= maxLen) {
            this.lastKeys.shift();
            this.lastValues.shift();
          }
          this.lastKeys.push(timeKey);
          this.lastValues.push(newVal);
        });

        const prevValues = this.lastValues.slice();
        this.lastValues = this.lastKeys.map((timeKey, index) => {
          if (Object.prototype.hasOwnProperty.call(newData, timeKey)) {
            return this.normalizePointValue(newData[timeKey]);
          }
          return prevValues[index] || [];
        });

        this.applyWindowToChart();
      } catch (error) {
        this.$message.error(error || '更新图表数据失败');
      }
    },
    renderChart (data) {
      this.chart = echarts.init(this.$refs.chart);
      this.chart.clear();

      if (!data || (Array.isArray(data) && !data.length)) {
        return;
      }

      // 按时间排序先
      const sortedArr = Object.keys(data)
        .sort((a, b) => (keyToTime(a) - keyToTime(b)))
        .map((key) => {
          let v = data[key];
          if (!v) {
            v = [];
          }
          if (!Array.isArray(v)) { // 把单系列也统一处理成数组形式
            v = [v];
          }
          return {
            time: key,
            value: v.map(vnum => (Number.isFinite(Number(vnum)) ? Number(vnum).toFixed(2) : '--'))
          };
        });
      const keys = sortedArr.map(item => item.time); // 一个 key 字符串数组
      const values = sortedArr.map(item => item.value); // 一个二维数组
      // console.log(222, keys, values, this.isEmpty);

      if (!keys.length || !values.length) return;

      const isLargeData = keys.length > 500;

      // 按系列的个数形成各自的数据
      const series = [];
      for (let i = 0; i < values[0].length; i++) {
        series.push({
          name: this.seriesNamesArr[i],
          data: values.map((subarr) => {
            const point = subarr[i];
            if (point === '--') return null;
            const n = Number(point);
            return Number.isFinite(n) ? n : null;
          }),
          type: 'line',
          large: isLargeData
        });
      }

      // 可选的图例名称替换
      const legendNameFormatter = this.legendNamesMap
        ? (serieName) => { // 每次调用的参数为单个系列名
          if (serieName in this.legendNamesMap) {
            return this.legendNamesMap[serieName];
          }
          return serieName;
        }
        : null;

      // 缩放设置
      const zoomArr = [{ type: 'inside' }];
      if (this.zoomBar) {
        zoomArr.push({
          type: 'slider',
          bottom: 30
        });
      }

      const cfg = {
        dataZoom: zoomArr,
        xAxis: {
          type: 'category',
          axisLine: {
            lineStyle: {
              color: '#E9E9E9'
            }
          },
          boundaryGap: !this.zoomBar,
          axisLabel: {
            textStyle: {
              color: 'rgba(0,0,0,.65)'
            },
            showMaxLabel: true,
          },
          axisTick: {
            inside: this.zoomBar,
          },
          data: keys
        },
        yAxis: {
          name: this.yAxisName,
          type: 'value',
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#E9E9E9'
            }
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: !this.zoomBar,
          },
          axisLabel: {
            textStyle: {
              color: 'rgba(0,0,0,.65)'
            }
          }
        },
        series,
        legend: {
          data: this.seriesNamesArr,
          bottom: 0,
          // selectedMode: false,
          formatter: legendNameFormatter
        },
        color: this.color,
        tooltip: {
          trigger: 'axis',
          formatter: (paramsArr) => {
            let cont = `${paramsArr[0].name}<br/>`;
            cont += paramsArr.map((params) => {
              const val = params.value;
              const displayVal = (val === null || val === undefined || Number.isNaN(Number(val))) ? '--' : val;
              return `${params.marker}${params.seriesName}: ${displayVal}${this.unit}`;
            }).join('<br/>');
            return cont;
          },
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: 'rgba(123,122,122,0.65)'
            }
          }
        },
        backgroundColor: '#fff',
        grid: {
          left: this.zoomBar ? '8%' : '5%',
          right: this.zoomBar ? '8%' : '5%',
          top: '15%',
          bottom: this.zoomBar ? '20%' : '15%'
        }
      };

      if (this.isEmpty) {
        cfg.yAxis.min = 0;
        cfg.yAxis.max = 100;
        cfg.dataZoom = null;
        cfg.tooltip = null;
      }

      this.chart.setOption(cfg);
      this.lastKeys = keys;
      this.lastValues = values;
    }
  } // end of methods
};

export default DynamicEnvChart;
</script>



// WEBPACK FOOTER //
// src/components/common/DynamicEnvChart.vue